import { SignUpParams } from '@aws-amplify/auth/src/types/Auth';
import { Box, Button, Card, CardContent, CircularProgress, Grid, TextField } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Auth } from 'aws-amplify';
import { AuthPiece } from 'aws-amplify-react';
import { useState } from 'react';
import InputMask from 'react-input-mask';
import useDebouncedEffect from 'use-debounced-effect';

import { JoiAuthSignup } from './custom.auth.joi';
import { useAuthStyles } from './styles';
import { useFormFields } from '../../../lib/hooks.lib';
import { ApplicationFormContainer } from '../../../private/pages/ApplicationsForm/applications-form.styles';

interface CustomSignUpProps {
  authState?: string;
  [key: string]: unknown;
}

interface ValidatedSignUpData {
  username: string;
  given_name: string;
  family_name: string;
  phone_number: string;
  email: string;
  password: string;
}

export const CustomSignUp = (props: CustomSignUpProps): JSX.Element => {
  const classes = useAuthStyles();
  const authPiece = new AuthPiece(props);
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [fields, handleFieldChange] = useFormFields({
    username: '',
    given_name: '',
    family_name: '',
    phone_number: '',
    email: '',
    password: ''
  });

  useDebouncedEffect(
    () => {
      if (fields.password && confirmPassword) {
        if (fields.password === confirmPassword) {
          setPasswordsMatch(true);
        } else {
          setPasswordsMatch(false);
        }
      } else {
        setPasswordsMatch(true);
      }
    },
    750,
    [fields.password, confirmPassword]
  );

  const checkFormFilled = () => {
    return Object.values(fields).every(Boolean) && passwordsMatch;
  };

  const toErrorMessages = (error: unknown): string[] => {
    if (error instanceof Error) {
      if (Array.isArray((error as Error & { message: unknown }).message)) {
        return (error as Error & { message: string[] }).message;
      }

      return [error.message];
    }

    return [String(error)];
  };

  const validateForm = async (): Promise<ValidatedSignUpData> => {
    try {
      return await JoiAuthSignup.validateAsync(fields);
    } catch (error) {
      throw toErrorMessages(error);
    }
  };

  const handleSignUp = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await validateForm();
      const formatData: SignUpParams = {
        username: data.username,
        password: data.password,
        attributes: {
          email: data.email,
          family_name: data.family_name,
          given_name: data.given_name,
          phone_number: data.phone_number
        }
      };
      await Auth.signUp(formatData);
      setErrorMessages([]);
      setLoading(false);
      authPiece.changeState('confirmSignUp', { username: data.username, phone: data.phone_number });
      setErrorMessages([]);
    } catch (error) {
      console.log(error);
      const parsedMessages = Array.isArray(error) ? (error as string[]) : toErrorMessages(error);
      setErrorMessages(parsedMessages);
      setLoading(false);
    }
  };

  const renderSignUp = () => (
    <ApplicationFormContainer>
      <Box ml={4} mr={4} mb={3}>
        {errorMessages.length > 0 && (
          <Alert
            severity="error"
            className={classes.alert}
            onClose={() => {
              setErrorMessages([]);
            }}
          >
            <AlertTitle>Validation Errors</AlertTitle>
            <ul>
              {errorMessages.map((item, i) => {
                return <li key={i}>{item}</li>;
              })}
            </ul>
          </Alert>
        )}
        <Grid container justifyContent="center">
          <Grid item>
            <Typography variant="h4">Create Account</Typography>
            <Typography variant="body1">Create an account to apply for a Texas Match the Promise Foundation℠ scholarship.</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box pt={3} mb={3}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={10} sm={5}>
            <Card>
              <CardContent>
                <form>
                  <Grid container justifyContent="space-between" spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        id="username"
                        name="username"
                        label="Username"
                        type="text"
                        fullWidth
                        value={fields.username}
                        onChange={handleFieldChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        id="given_name"
                        name="given_name"
                        label="First Name"
                        type="text"
                        fullWidth
                        value={fields.given_name}
                        onChange={handleFieldChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        id="family_name"
                        name="family_name"
                        label="Last Name"
                        type="text"
                        fullWidth
                        value={fields.family_name}
                        onChange={handleFieldChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <InputMask
                        mask="+1-999-999-9999"
                        value={fields.phone_number}
                        disabled={false}
                        maskChar=" "
                        onChange={(evt) => {
                          evt.target.value = evt.target.value.replace(/-/g, '');
                          handleFieldChange(evt);
                        }}
                      >
                        {() => (
                          <TextField name="phone_number" required id="phone_number" label="Mobile Phone (will receive a text)" fullWidth />
                        )}
                      </InputMask>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        id="email"
                        name="email"
                        label="Email"
                        type="text"
                        fullWidth
                        value={fields.email}
                        onChange={handleFieldChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        error={!passwordsMatch}
                        id="password"
                        name="password"
                        label="Password"
                        type="password"
                        fullWidth
                        value={fields.password}
                        onChange={handleFieldChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        error={!passwordsMatch}
                        helperText={!passwordsMatch && 'Passwords do not match.'}
                        id="confirmPassword"
                        name="confirmPassword"
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <p className={classes.helpText}>
                        At least 8 characters with mix of upper and lowercase letters, numbers and special characters.
                      </p>
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="center">
                    <Grid item style={{ marginTop: '20px' }}>
                      <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        className={classes.button}
                        disabled={!checkFormFilled()}
                        onClick={(e) => handleSignUp(e)}
                      >
                        Sign Up
                        {loading && (
                          <Box ml={2} mt={1}>
                            <CircularProgress size="0.8rem" color={'inherit'} />
                          </Box>
                        )}
                      </Button>
                    </Grid>
                  </Grid>

                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      authPiece.changeState('signIn');
                      setErrorMessages([]);
                    }}
                    variant="body2"
                  >
                    Back to sign in
                  </Link>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ApplicationFormContainer>
  );

  return <div>{props.authState === 'signUp' && renderSignUp()}</div>;
};
